import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import { GATE_TITLE, formatDate, formatNumber } from './format';
import { startTestEvidenceSync, uploadSummary, useConfidence } from './useReleaseCadence';
import { GateChip, KindChip, TestBar } from './ConfidenceParts';
import AreaHeatmap from './AreaHeatmap';
import Scorecard from './Scorecard';
import ReleaseDrawer from './ReleaseDrawer';

const POLL_MS = 8000;
const DECISION_LABEL = { ready: 'Go', conditional: 'Conditional Go', hold: 'No-Go', 'not-set': 'Not set' };
const CARD_SX = { borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)' };
const HEAD_SX = { bgcolor: '#F8F9FA', '& th': { color: DEEP_GREEN, fontWeight: 600, borderBottom: '2px solid rgba(26,46,34,0.15)', whiteSpace: 'nowrap' } };

const Stat = ({ label, value, hint }) => (
  <Tooltip title={hint || ''} placement="top" disableHoverListener={!hint}>
    <Box sx={{ pr: 3, mr: 3, borderRight: '1px solid rgba(0,0,0,0.08)', '&:last-of-type': { border: 0, mr: 0, pr: 0 } }}>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: DEEP_GREEN, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="caption" sx={{ color: MUTED }}>{label}</Typography>
    </Box>
  </Tooltip>
);

const Kpis = ({ kpis }) => (
  <Paper variant="outlined" sx={{ ...CARD_SX, p: 2.5, display: 'flex', flexWrap: 'wrap', rowGap: 2 }}>
    <Stat label="Releases shipped" value={formatNumber(kpis.releases)} hint="Versions that have been rolled out or are dated before today." />
    <Stat label="With test evidence" value={`${kpis.withTestEvidence} of ${kpis.releases}`} hint="Shipped releases that have a test plan in Azure DevOps." />
    <Stat label="Tests planned, all releases" value={formatNumber(kpis.testsPlanned)} hint="Test points across all shipped releases that have a plan." />
    <Stat label="Tests passed, all releases" value={formatNumber(kpis.testsPassed)} hint="Test points that passed across the same releases." />
    <Stat label="Go decisions" value={formatNumber(kpis.goDecisionsRecorded)} hint="Releases with a Go decision recorded in the portal or printed in their release summary." />
    <Stat label="Typical gap" value={kpis.medianGapDays == null ? '–' : `${kpis.medianGapDays} d`} hint="Median days between consecutive non-hotfix releases." />
  </Paper>
);

const ReleaseRow = ({ release, selected, onSelect }) => (
  <TableRow
    hover
    selected={selected}
    onClick={() => onSelect(release.version)}
    sx={{ cursor: 'pointer', '& td': { borderBottomColor: 'rgba(0,0,0,0.05)' }, ...(release.stage === 'upcoming' && { bgcolor: 'rgba(95,125,140,0.05)' }) }}
  >
    <TableCell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: DEEP_GREEN }}>{release.version}</Typography>
        <KindChip kind={release.kind} />
      </Box>
    </TableCell>
    <TableCell sx={{ color: MUTED, whiteSpace: 'nowrap' }}>
      {release.stage === 'upcoming' ? <em>Upcoming</em> : formatDate(release.date)}
    </TableCell>
    <TableCell><TestBar totals={release.tests && release.tests.totals} /></TableCell>
    <TableCell><GateChip gate="tested" status={release.gates.tested.status} compact /></TableCell>
    <TableCell><GateChip gate="approved" status={release.gates.approved.status} compact /></TableCell>
    <TableCell><GateChip gate="rolledOut" status={release.gates.rolledOut.status} compact /></TableCell>
  </TableRow>
);

const ReleaseTrain = ({ releases, selected, onSelect }) => (
  <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
    <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, p: 2, pb: 0.5 }}>Release train</Typography>
    <Typography variant="body2" sx={{ color: MUTED, px: 2, pb: 1 }}>
      Every release passes three gates: tested, approved and rolled out. Select a release for the evidence behind each gate.
    </Typography>
    <Table size="small">
      <TableHead>
        <TableRow sx={HEAD_SX}>
          <TableCell>Release</TableCell>
          <TableCell>Date</TableCell>
          <TableCell>Test results</TableCell>
          <TableCell>{GATE_TITLE.tested}</TableCell>
          <TableCell>{GATE_TITLE.approved}</TableCell>
          <TableCell>{GATE_TITLE.rolledOut}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {releases.map((r) => <ReleaseRow key={r.version} release={r} selected={selected === r.version} onSelect={onSelect} />)}
      </TableBody>
    </Table>
  </Paper>
);

const SyncLine = ({ testSync, onSync, starting, error }) => {
  const syncing = starting || (testSync && testSync.syncing);
  const last = testSync && testSync.lastSync;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Button size="small" variant="outlined" onClick={onSync} disabled={syncing} startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />} sx={{ textTransform: 'none', borderRadius: '10px' }}>
        {syncing ? 'Reading test plans…' : 'Refresh test results'}
      </Button>
      {error && <Typography variant="body2" color="error">{error}</Typography>}
      {!error && last && last.error && <Typography variant="body2" color="error">Last refresh failed: {last.error}</Typography>}
      {!error && last && !last.error && (
        <Typography variant="caption" sx={{ color: MUTED }}>
          Last refresh {formatDate(last.at)}: {last.synced} plans read{last.failed && last.failed.length ? `, ${last.failed.length} failed` : ''}. Refreshes automatically every night.
        </Typography>
      )}
    </Box>
  );
};

const SummaryUpload = ({ onDone }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setBusy(true);
    const out = [];
    for (const file of files) {
      try {
        const r = await uploadSummary(file);
        out.push({ ok: true, text: `${r.version}: ${DECISION_LABEL[r.decision] || r.decision}` });
      } catch (err) {
        out.push({ ok: false, text: `${file.name}: ${err.message}` });
      }
    }
    setResults(out);
    setBusy(false);
    await onDone();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <input ref={input} type="file" accept="application/pdf,.pdf" multiple hidden onChange={handleFiles} />
      <Tooltip title="Reads the Go / No-Go decision printed in each release summary PDF." placement="top">
        <span>
          <Button size="small" variant="outlined" onClick={() => input.current && input.current.click()} disabled={busy} startIcon={busy ? <CircularProgress size={14} /> : <UploadFileIcon />} sx={{ textTransform: 'none', borderRadius: '10px' }}>
            {busy ? 'Reading summaries…' : 'Upload release summaries'}
          </Button>
        </span>
      </Tooltip>
      {results.map((r) => (
        <Typography key={r.text} variant="caption" color={r.ok ? undefined : 'error'} sx={r.ok ? { color: MUTED } : undefined}>{r.text}</Typography>
      ))}
    </Box>
  );
};

const ConfidenceView = ({ onOpenPlans }) => {
  const [customerView, setCustomerView] = useState(false);
  const audience = customerView ? 'customer' : 'internal';
  const { data, loading, error, refreshError, reload } = useConfidence(audience);
  const [selected, setSelected] = useState(null);
  const [starting, setStarting] = useState(false);
  const [syncError, setSyncError] = useState('');

  const syncing = !!(data && data.testSync && data.testSync.syncing);
  useEffect(() => {
    if (!syncing) return undefined;
    const timer = setInterval(reload, POLL_MS);
    return () => clearInterval(timer);
  }, [syncing, reload]);

  const handleSync = async () => {
    setStarting(true);
    setSyncError('');
    try {
      await startTestEvidenceSync();
      await reload();
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setStarting(false);
    }
  };

  const releases = useMemo(() => (data ? [...data.releases].reverse() : []), [data]);
  const current = useMemo(() => releases.find((r) => r.version === selected) || null, [releases, selected]);

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        {customerView || !data ? (
          <Box />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <SyncLine testSync={data.testSync} onSync={handleSync} starting={starting} error={syncError} />
            <SummaryUpload onDone={reload} />
          </Box>
        )}
        <Tooltip title="Hides internal detail such as failing test titles and approver names, so the page can be shown to customers." placement="left">
          <FormControlLabel
            control={<Switch checked={customerView} onChange={(e) => { setCustomerView(e.target.checked); setSelected(null); }} color="success" />}
            label="Customer view"
            sx={{ mr: 0 }}
          />
        </Tooltip>
      </Box>
      {refreshError && <Alert severity="warning">Could not refresh: {refreshError}. Showing the last data loaded.</Alert>}
      {loading && !data && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {data && !data.releases.length && <Alert severity="info">No release evidence yet. Use “Refresh test results” to read the Azure DevOps test plans.</Alert>}
      {data && data.releases.length > 0 && (
        <>
          <Scorecard scorecard={data.scorecard} onSelect={setSelected} />
          <Kpis kpis={data.kpis} />
          <ReleaseTrain releases={releases} selected={selected} onSelect={setSelected} />
          <AreaHeatmap releases={releases} onSelect={setSelected} />
        </>
      )}
      <ReleaseDrawer release={current} customerView={customerView} onClose={() => setSelected(null)} onOpenPlans={onOpenPlans} />
    </Box>
  );
};

export default ConfidenceView;
